import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import { deleteMySmtp, saveMySmtp } from '../../services/api/implementation/impl';
import { MY_SMTP_STATUS_QUERY_KEY } from '../../utils/emailSendersAccess';
import { EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY } from '../../utils/emailLeadsAccess';
import AppPasswordHelp from './AppPasswordHelp';

/**
 * The logged-in user's own Gmail, used for AI Insights emails. The Profile
 * page still has the same control; both read and write the same endpoint and
 * cache key, so a change in one shows in the other.
 */
export default function PersonalSender({ personal, isPending }) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const connected = Boolean(personal?.connected);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (payload) => saveMySmtp(payload),
    onSuccess: () => {
      toast.success('Gmail connected for AI Insights emails');
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: MY_SMTP_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to connect Gmail');
    }
  });

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationFn: () => deleteMySmtp(),
    onSuccess: () => {
      toast.success('Gmail disconnected');
      queryClient.invalidateQueries({ queryKey: MY_SMTP_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to disconnect Gmail');
    }
  });

  const open = () => {
    // The status endpoint may only return a masked address; never prefill that.
    const knownEmail =
      personal?.fromEmail && !personal.fromEmail.includes('*') ? personal.fromEmail : '';
    reset({
      smtp_host: 'smtp.gmail.com',
      smtp_port: 465,
      smtp_secure: true,
      smtp_user: knownEmail,
      smtp_password: '',
      smtp_from_email: knownEmail,
      smtp_from_name: personal?.fromName || ''
    });
    setIsOpen(true);
  };

  const onSubmit = (data) => {
    save({
      ...data,
      smtp_host: data.smtp_host || 'smtp.gmail.com',
      smtp_port: Number(data.smtp_port) || 465,
      smtp_secure: data.smtp_secure !== false && data.smtp_secure !== 'false'
    });
  };

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Your personal sender</h3>
        <p className="text-sm text-gray-600">
          AI Insights emails you send go out from your own Gmail. Only you can see or change this.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-6 shadow-md">
        <div className="text-sm">
          {isPending ? (
            <span className="text-gray-400">Checking connection…</span>
          ) : connected ? (
            <span className="text-teal-800">
              Connected as <strong>{personal.fromEmailMasked || personal.fromEmail}</strong>
              {personal.fromName ? ` (${personal.fromName})` : ''}
            </span>
          ) : (
            <span className="text-amber-700">Not connected — AI email send is blocked</span>
          )}
        </div>
        <div className="flex gap-2">
          {connected ? (
            <Button
              type="button"
              displayType="secondary"
              disabled={isDisconnecting}
              onClick={() => disconnect()}>
              {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          ) : null}
          <Button type="button" onClick={open} disabled={isSaving || isPending}>
            {connected ? 'Update Gmail' : 'Connect Gmail'}
          </Button>
        </div>
      </div>

      {isOpen ? (
        <Modal
          name={connected ? 'Update Gmail SMTP' : 'Connect Gmail SMTP'}
          onClose={() => !isSaving && setIsOpen(false)}
          width={40}
          isDirty={true}
          confirmOnClose={false}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <AppPasswordHelp />
            <div className="grid grid-cols-1 gap-4">
              <FormInput
                label="SMTP User (Gmail)"
                register_key="smtp_user"
                register={register}
                errors={errors.smtp_user}
                isRequired={true}
                readOnly={isSaving}
                width={100}
              />
              <FormInput
                label="SMTP Password (App Password)"
                register_key="smtp_password"
                type="password"
                register={register}
                errors={errors.smtp_password}
                isRequired={true}
                readOnly={isSaving}
                width={100}
              />
              <FormInput
                label="From Email"
                register_key="smtp_from_email"
                register={register}
                errors={errors.smtp_from_email}
                isRequired={true}
                readOnly={isSaving}
                width={100}
              />
              <FormInput
                label="From Name"
                register_key="smtp_from_name"
                register={register}
                errors={errors.smtp_from_name}
                isRequired={true}
                readOnly={isSaving}
                width={100}
              />
            </div>
            <div className="mt-6 flex justify-end gap-4">
              <Button
                type="button"
                displayType="secondary"
                disabled={isSaving}
                onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Connecting…' : 'Connect'}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}
